import { Ban } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BlockedNumber,
  findBlockedNumber,
  normalizeBlockedNumber,
  useBlockedNumbers,
} from '~/atoms/BlockedNumbersState'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { DropdownActionButton } from '~/components/shared/Dropdown/DropdownActionButton'
import { TextButtonMenuItem } from '~/components/shared/Dropdown/DropdownTextButton'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ContactNumber } from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

export interface BlockContactQuickActionButtonProps {
  numbers: ContactNumber[]
}

interface BlockableContactNumber {
  contactNumber: ContactNumber
  formattedNumber: string
  blockedNumber?: BlockedNumber
}

export const BlockContactQuickActionButton = ({
  numbers,
}: BlockContactQuickActionButtonProps) => {
  const { t } = useTranslation()
  const {
    blockedNumbers,
    blockNumberWithConfirmation: blockNumber,
    unblockNumber,
  } = useBlockedNumbers()

  const blockableNumbers = useMemo(() => {
    if (!numbers || numbers.length === 0) {
      return []
    }

    return numbers
      .map((contactNumber) => {
        const formattedNumber = normalizeBlockedNumber(contactNumber.number)
        return {
          contactNumber,
          formattedNumber,
          blockedNumber: findBlockedNumber(
            blockedNumbers,
            contactNumber.number,
          ),
        } as BlockableContactNumber
      })
      .filter(
        (contactNumber) =>
          contactNumber.formattedNumber.replace(/\D+/g, '').length >= 10,
      )
  }, [numbers, blockedNumbers])

  const menuItems = useMemo(() => {
    return (
      blockableNumbers?.map((blockableNumber) => {
        return {
          content: (
            <MenuListItem
              key={blockableNumber.contactNumber.id}
              primaryText={`${blockableNumber.blockedNumber ? 'Unblock' : 'Block'} ${formatPhoneNumber(blockableNumber.contactNumber.number)}`}
              secondaryText={blockableNumber.contactNumber.label}
            />
          ),
          value: blockableNumber,
        } as TextButtonMenuItem<BlockableContactNumber>
      }) ?? []
    )
  }, [blockableNumbers])

  const handleItemSelected = ({
    contactNumber,
    blockedNumber,
  }: BlockableContactNumber) => {
    if (blockedNumber) {
      unblockNumber(blockedNumber.id, blockedNumber.phoneNumber)
    } else {
      blockNumber(contactNumber.number)
    }
  }

  return (
    <DropdownActionButton
      key="block-contacts"
      icon={Ban}
      tooltipText={t('blockedNumber.blockNumbers', 'Block Numbers')}
      variant={QuickActionButtonVariants.Default_200}
      menuOptions={{
        width: '16rem',
        placement: MenuPlacement.BottomRight,
      }}
      size={Size.md}
      items={menuItems}
      onSelect={handleItemSelected}
    />
  )
}
